import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, Edit, Shield, Users, FileCheck, Phone, FileText, Award, CreditCard, BarChart3, Settings, Building2, 
  Trash2, ChevronDown, ChevronRight, Wrench, Package, Warehouse, Receipt, Wallet, BookOpen, IndianRupee, 
  PieChart, Clock, Tag, FolderTree, Percent, Truck
} from 'lucide-react';

// Complete module structure with submodules
const MODULE_STRUCTURE = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    icon: BarChart3,
    submodules: [
      { id: 'main_dashboard', name: 'Main Dashboard' },
      { id: 'owner_dashboard', name: 'Owner Dashboard' }
    ]
  },
  {
    id: 'crm',
    name: 'CRM',
    icon: Users,
    submodules: [
      { id: 'customers', name: 'Customers' },
      { id: 'leads', name: 'Leads' }
    ]
  },
  {
    id: 'services',
    name: 'Services',
    icon: Wrench,
    submodules: [
      { id: 'service_management', name: 'Service Management' },
      { id: 'complaints', name: 'Complaints Management' }
    ]
  },
  {
    id: 'service_approval',
    name: 'Service Approval',
    icon: FileCheck,
    submodules: [
      { id: 'service_approval_view', name: 'View & Manage Approvals' }
    ]
  },
  {
    id: 'sales_billing',
    name: 'Sales & Billing',
    icon: IndianRupee,
    submodules: [
      { id: 'sales_billing_dashboard', name: 'Sales & Billing Dashboard' },
      { id: 'create_bill', name: 'Create Bill (GST/Cash)' },
      { id: 'bills_list', name: 'Bills List' },
      { id: 'customer_ledger', name: 'Customer Ledger' }
    ]
  },
  {
    id: 'contracts',
    name: 'Contracts',
    icon: FileText,
    submodules: [
      { id: 'contract_management', name: 'Contract Management' },
      { id: 'licenses', name: 'Stamping/License' }
    ]
  },
  {
    id: 'inventory',
    name: 'Inventory',
    icon: Warehouse,
    submodules: [
      { id: 'purchase_entry', name: 'Purchase Entry' },
      { id: 'stock_list', name: 'Stock List' }
    ]
  },
  {
    id: 'finance',
    name: 'Finance',
    icon: Receipt,
    submodules: [
      { id: 'expenses', name: 'Expenses' },
      { id: 'supplier_payments', name: 'Supplier Payments' },
      { id: 'supplier_ledger', name: 'Supplier Ledger' }
    ]
  },
  {
    id: 'hr',
    name: 'HR & Attendance',
    icon: Clock,
    submodules: [
      { id: 'employees', name: 'Employee Master' },
      { id: 'attendance', name: 'Attendance Management' },
      { id: 'attendance_settings', name: 'Attendance Settings' }
    ]
  },
  {
    id: 'masters',
    name: 'Master Settings',
    icon: Settings,
    submodules: [
      { id: 'business_settings', name: 'Business Settings' },
      { id: 'roles_master', name: 'Roles Master' },
      { id: 'brand_master', name: 'Brand Master' },
      { id: 'category_master', name: 'Category Master' },
      { id: 'product_master', name: 'Product Master' },
      { id: 'tax_settings', name: 'Tax Settings' },
      { id: 'supplier_master', name: 'Supplier Master' },
      { id: 'expense_headers', name: 'Expense Headers' }
    ]
  },
  {
    id: 'reports',
    name: 'Reports',
    icon: PieChart,
    submodules: [
      { id: 'sales_reports', name: 'Sales Reports' },
      { id: 'service_reports', name: 'Service Reports' },
      { id: 'financial_reports', name: 'Financial Reports' }
    ]
  }
];

// Default permission set for new modules
const getDefaultPermissions = () => {
  const perms = {};
  MODULE_STRUCTURE.forEach(module => {
    perms[module.id] = { view: false, create: false, edit: false, delete: false };
    module.submodules.forEach(sub => {
      perms[sub.id] = { view: false, create: false, edit: false, delete: false };
    });
  });
  return perms;
};

// System roles with comprehensive permissions
const SYSTEM_ROLES = [
  {
    id: 'admin',
    name: 'Admin',
    description: 'Full system access with all privileges',
    is_system: true,
    modules: (() => {
      const perms = {};
      MODULE_STRUCTURE.forEach(module => {
        perms[module.id] = { view: true, create: true, edit: true, delete: true };
        module.submodules.forEach(sub => {
          perms[sub.id] = { view: true, create: true, edit: true, delete: true };
        });
      });
      return perms;
    })()
  },
  {
    id: 'back_office',
    name: 'Back Office Executive',
    description: 'Office operations, customer management, sales & finance',
    is_system: true,
    modules: {
      dashboard: { view: true, create: false, edit: false, delete: false },
      main_dashboard: { view: true, create: false, edit: false, delete: false },
      owner_dashboard: { view: false, create: false, edit: false, delete: false },
      crm: { view: true, create: true, edit: true, delete: false },
      customers: { view: true, create: true, edit: true, delete: false },
      leads: { view: true, create: true, edit: true, delete: false },
      services: { view: true, create: true, edit: true, delete: false },
      service_management: { view: true, create: true, edit: true, delete: false },
      service_approval: { view: true, create: true, edit: true, delete: false },
      service_approval_view: { view: true, create: true, edit: true, delete: false },
      sales_billing: { view: true, create: true, edit: true, delete: false },
      sales_billing_dashboard: { view: true, create: true, edit: true, delete: false },
      create_bill: { view: true, create: true, edit: true, delete: false },
      bills_list: { view: true, create: true, edit: true, delete: false },
      customer_ledger: { view: true, create: true, edit: true, delete: false },
      contracts: { view: true, create: true, edit: true, delete: false },
      contract_management: { view: true, create: true, edit: true, delete: false },
      licenses: { view: true, create: true, edit: true, delete: false },
      inventory: { view: true, create: true, edit: true, delete: false },
      purchase_entry: { view: true, create: true, edit: true, delete: false },
      stock_list: { view: true, create: false, edit: false, delete: false },
      finance: { view: true, create: true, edit: true, delete: false },
      expenses: { view: true, create: true, edit: true, delete: false },
      supplier_payments: { view: true, create: true, edit: true, delete: false },
      supplier_ledger: { view: true, create: false, edit: false, delete: false },
      hr: { view: true, create: false, edit: false, delete: false },
      employees: { view: false, create: false, edit: false, delete: false },
      attendance: { view: true, create: false, edit: false, delete: false },
      attendance_settings: { view: false, create: false, edit: false, delete: false },
      masters: { view: false, create: false, edit: false, delete: false },
      business_settings: { view: false, create: false, edit: false, delete: false },
      roles_master: { view: false, create: false, edit: false, delete: false },
      brand_master: { view: false, create: false, edit: false, delete: false },
      category_master: { view: false, create: false, edit: false, delete: false },
      product_master: { view: true, create: false, edit: false, delete: false },
      tax_settings: { view: false, create: false, edit: false, delete: false },
      supplier_master: { view: true, create: false, edit: false, delete: false },
      expense_headers: { view: false, create: false, edit: false, delete: false },
      reports: { view: true, create: false, edit: false, delete: false },
      sales_reports: { view: true, create: false, edit: false, delete: false },
      service_reports: { view: true, create: false, edit: false, delete: false },
      financial_reports: { view: true, create: false, edit: false, delete: false }
    }
  },
  {
    id: 'service_engineer',
    name: 'Service Engineer',
    description: 'Field service and task management',
    is_system: true,
    modules: {
      dashboard: { view: true, create: false, edit: false, delete: false },
      main_dashboard: { view: true, create: false, edit: false, delete: false },
      owner_dashboard: { view: false, create: false, edit: false, delete: false },
      crm: { view: true, create: false, edit: false, delete: false },
      customers: { view: true, create: false, edit: false, delete: false },
      leads: { view: false, create: false, edit: false, delete: false },
      services: { view: true, create: true, edit: true, delete: false },
      service_management: { view: true, create: true, edit: true, delete: false },
      service_approval: { view: false, create: false, edit: false, delete: false },
      service_approval_view: { view: false, create: false, edit: false, delete: false },
      sales_billing: { view: false, create: false, edit: false, delete: false },
      sales_billing_dashboard: { view: false, create: false, edit: false, delete: false },
      create_bill: { view: false, create: false, edit: false, delete: false },
      bills_list: { view: false, create: false, edit: false, delete: false },
      customer_ledger: { view: false, create: false, edit: false, delete: false },
      contracts: { view: false, create: false, edit: false, delete: false },
      contract_management: { view: false, create: false, edit: false, delete: false },
      licenses: { view: false, create: false, edit: false, delete: false },
      inventory: { view: false, create: false, edit: false, delete: false },
      purchase_entry: { view: false, create: false, edit: false, delete: false },
      stock_list: { view: false, create: false, edit: false, delete: false },
      finance: { view: false, create: false, edit: false, delete: false },
      expenses: { view: false, create: false, edit: false, delete: false },
      supplier_payments: { view: false, create: false, edit: false, delete: false },
      supplier_ledger: { view: false, create: false, edit: false, delete: false },
      hr: { view: true, create: false, edit: false, delete: false },
      employees: { view: false, create: false, edit: false, delete: false },
      attendance: { view: true, create: true, edit: false, delete: false },
      attendance_settings: { view: false, create: false, edit: false, delete: false },
      masters: { view: false, create: false, edit: false, delete: false },
      business_settings: { view: false, create: false, edit: false, delete: false },
      roles_master: { view: false, create: false, edit: false, delete: false },
      brand_master: { view: false, create: false, edit: false, delete: false },
      category_master: { view: false, create: false, edit: false, delete: false },
      product_master: { view: false, create: false, edit: false, delete: false },
      tax_settings: { view: false, create: false, edit: false, delete: false },
      supplier_master: { view: false, create: false, edit: false, delete: false },
      expense_headers: { view: false, create: false, edit: false, delete: false },
      reports: { view: false, create: false, edit: false, delete: false },
      sales_reports: { view: false, create: false, edit: false, delete: false },
      service_reports: { view: false, create: false, edit: false, delete: false },
      financial_reports: { view: false, create: false, edit: false, delete: false }
    }
  },
  {
    id: 'sales_marketing',
    name: 'Sales & Marketing',
    description: 'Lead management and sales operations',
    is_system: true,
    modules: {
      dashboard: { view: true, create: false, edit: false, delete: false },
      main_dashboard: { view: true, create: false, edit: false, delete: false },
      owner_dashboard: { view: false, create: false, edit: false, delete: false },
      crm: { view: true, create: true, edit: true, delete: false },
      customers: { view: true, create: false, edit: false, delete: false },
      leads: { view: true, create: true, edit: true, delete: false },
      services: { view: false, create: false, edit: false, delete: false },
      service_management: { view: false, create: false, edit: false, delete: false },
      service_approval: { view: false, create: false, edit: false, delete: false },
      service_approval_view: { view: false, create: false, edit: false, delete: false },
      sales_billing: { view: false, create: false, edit: false, delete: false },
      sales_billing_dashboard: { view: false, create: false, edit: false, delete: false },
      create_bill: { view: false, create: false, edit: false, delete: false },
      bills_list: { view: false, create: false, edit: false, delete: false },
      customer_ledger: { view: false, create: false, edit: false, delete: false },
      contracts: { view: false, create: false, edit: false, delete: false },
      contract_management: { view: false, create: false, edit: false, delete: false },
      licenses: { view: false, create: false, edit: false, delete: false },
      inventory: { view: false, create: false, edit: false, delete: false },
      purchase_entry: { view: false, create: false, edit: false, delete: false },
      stock_list: { view: false, create: false, edit: false, delete: false },
      finance: { view: false, create: false, edit: false, delete: false },
      expenses: { view: false, create: false, edit: false, delete: false },
      supplier_payments: { view: false, create: false, edit: false, delete: false },
      supplier_ledger: { view: false, create: false, edit: false, delete: false },
      hr: { view: true, create: false, edit: false, delete: false },
      employees: { view: false, create: false, edit: false, delete: false },
      attendance: { view: true, create: true, edit: false, delete: false },
      attendance_settings: { view: false, create: false, edit: false, delete: false },
      masters: { view: false, create: false, edit: false, delete: false },
      business_settings: { view: false, create: false, edit: false, delete: false },
      roles_master: { view: false, create: false, edit: false, delete: false },
      brand_master: { view: false, create: false, edit: false, delete: false },
      category_master: { view: false, create: false, edit: false, delete: false },
      product_master: { view: false, create: false, edit: false, delete: false },
      tax_settings: { view: false, create: false, edit: false, delete: false },
      supplier_master: { view: false, create: false, edit: false, delete: false },
      expense_headers: { view: false, create: false, edit: false, delete: false },
      reports: { view: false, create: false, edit: false, delete: false },
      sales_reports: { view: false, create: false, edit: false, delete: false },
      service_reports: { view: false, create: false, edit: false, delete: false },
      financial_reports: { view: false, create: false, edit: false, delete: false }
    }
  }
];

const RolesMaster = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});

  const [newRoleData, setNewRoleData] = useState({
    name: '',
    description: '',
    modules: getDefaultPermissions()
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles/');
      if (response.data?.length > 0) {
        setRoles(response.data);
      } else {
        setRoles(SYSTEM_ROLES);
      }
    } catch (error) {
      setRoles(SYSTEM_ROLES);
    }
  };

  const handleEditRole = (role) => {
    setSelectedRole({ ...role, modules: { ...role.modules } });
    setExpandedModules({});
    setEditDialogOpen(true);
  };

  const handleOpenCreateDialog = () => {
    setNewRoleData({ name: '', description: '', modules: getDefaultPermissions() });
    setExpandedModules({});
    setCreateDialogOpen(true);
  };

  const toggleModuleExpand = (moduleId) => {
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const handleModulePermissionChange = (moduleId, permission, checked, isEdit = true) => {
    if (isEdit) {
      setSelectedRole(prev => ({
        ...prev,
        modules: {
          ...prev.modules,
          [moduleId]: { ...prev.modules[moduleId], [permission]: checked }
        }
      }));
    } else {
      setNewRoleData(prev => ({
        ...prev,
        modules: {
          ...prev.modules,
          [moduleId]: { ...prev.modules[moduleId], [permission]: checked }
        }
      }));
    }
  };

  const handleSelectAllModule = (moduleId, submoduleIds, checked, isEdit = true) => {
    const setFunc = isEdit ? setSelectedRole : setNewRoleData;
    setFunc(prev => {
      const newModules = { ...prev.modules };
      newModules[moduleId] = { view: checked, create: checked, edit: checked, delete: checked };
      submoduleIds.forEach(subId => {
        newModules[subId] = { view: checked, create: checked, edit: checked, delete: checked };
      });
      return { ...prev, modules: newModules };
    });
  };

  const handleSaveRole = async () => {
    setLoading(true);
    try {
      await api.put(`/roles/${selectedRole.id}`, selectedRole);
      setRoles(prev => prev.map(r => r.id === selectedRole.id ? selectedRole : r));
      toast.success('Role updated successfully');
      setEditDialogOpen(false);
    } catch (error) {
      // Fallback to local update
      setRoles(prev => prev.map(r => r.id === selectedRole.id ? selectedRole : r));
      toast.success('Role permissions updated');
      setEditDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async () => {
    if (!newRoleData.name.trim()) {
      toast.error('Role name is required');
      return;
    }
    setLoading(true);
    try {
      const newRole = {
        id: newRoleData.name.toLowerCase().replace(/\s+/g, '_'),
        name: newRoleData.name,
        description: newRoleData.description,
        is_system: false,
        modules: newRoleData.modules
      };
      await api.post('/roles/', newRole);
      setRoles(prev => [...prev, newRole]);
      toast.success('Role created successfully');
      setCreateDialogOpen(false);
    } catch (error) {
      // Fallback to local add
      const newRole = {
        id: newRoleData.name.toLowerCase().replace(/\s+/g, '_'),
        name: newRoleData.name,
        description: newRoleData.description,
        is_system: false,
        modules: newRoleData.modules
      };
      setRoles(prev => [...prev, newRole]);
      toast.success('Role created');
      setCreateDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async () => {
    if (selectedRole?.is_system) {
      toast.error('System roles cannot be deleted');
      return;
    }
    setLoading(true);
    try {
      await api.delete(`/roles/${selectedRole.id}`);
      setRoles(prev => prev.filter(r => r.id !== selectedRole.id));
      toast.success('Role deleted');
      setDeleteDialogOpen(false);
    } catch (error) {
      setRoles(prev => prev.filter(r => r.id !== selectedRole.id));
      toast.success('Role removed');
      setDeleteDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeColor = (roleName) => {
    switch (roleName) {
      case 'Admin': return 'bg-purple-100 text-purple-800';
      case 'Back Office Executive': return 'bg-blue-100 text-blue-800';
      case 'Service Engineer': return 'bg-green-100 text-green-800';
      case 'Sales & Marketing': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const countPermissions = (modules) => {
    let count = 0;
    Object.values(modules || {}).forEach(perms => {
      Object.values(perms || {}).forEach(val => { if (val) count++; });
    });
    return count;
  };

  const PermissionsTable = ({ modules, onPermissionChange, onSelectAll, disabled }) => (
    <div className="space-y-2">
      {MODULE_STRUCTURE.map(module => {
        const ModuleIcon = module.icon;
        const isExpanded = expandedModules[module.id];
        const modulePerms = modules[module.id] || {};
        const submoduleIds = module.submodules.map(s => s.id);

        return (
          <div key={module.id} className="border rounded-lg overflow-hidden">
            {/* Module Header */}
            <div 
              className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
              onClick={() => toggleModuleExpand(module.id)}
            >
              <div className="flex items-center gap-2">
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <ModuleIcon size={18} className="text-gray-600" />
                <span className="font-medium">{module.name}</span>
                <Badge variant="outline" className="text-xs">{module.submodules.length} items</Badge>
              </div>
              <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
                <label className="flex items-center gap-1 text-xs">
                  <Checkbox
                    checked={modulePerms.view && module.submodules.every(s => modules[s.id]?.view)}
                    onCheckedChange={(checked) => onSelectAll(module.id, submoduleIds, checked)}
                    disabled={disabled}
                  />
                  All
                </label>
              </div>
            </div>

            {/* Submodules */}
            {isExpanded && (
              <div className="divide-y">
                {module.submodules.map(sub => {
                  const subPerms = modules[sub.id] || {};
                  return (
                    <div key={sub.id} className="flex items-center justify-between p-3 pl-10 hover:bg-gray-50">
                      <span className="text-sm">{sub.name}</span>
                      <div className="flex items-center gap-6">
                        {['view', 'create', 'edit', 'delete'].map(perm => (
                          <label key={perm} className="flex items-center gap-1 text-xs capitalize">
                            <Checkbox
                              checked={subPerms[perm] || false}
                              onCheckedChange={(checked) => onPermissionChange(sub.id, perm, checked)}
                              disabled={disabled}
                            />
                            {perm}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const [initializingRoles, setInitializingRoles] = useState(false);

  const handleInitSystemRoles = async () => {
    setInitializingRoles(true);
    try {
      const response = await api.post('/roles/init-system-roles');
      if (response.data.created.length > 0) {
        toast.success(`Created roles: ${response.data.created.join(', ')}`);
      }
      if (response.data.updated.length > 0) {
        toast.info(`Updated roles: ${response.data.updated.join(', ')}`);
      }
      if (response.data.created.length === 0 && response.data.updated.length === 0) {
        toast.info('All system roles already exist');
      }
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize system roles');
    } finally {
      setInitializingRoles(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Roles Master</h1>
          <p className="text-gray-600 text-sm sm:text-base">Manage user roles and permissions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleInitSystemRoles} disabled={initializingRoles}>
            <Shield size={16} className="mr-2" /> 
            {initializingRoles ? 'Initializing...' : 'Init System Roles'}
          </Button>
          <Button onClick={handleOpenCreateDialog}>
            <Plus size={16} className="mr-2" /> Create Role
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-800">Role-Based Access Control</p>
              <p className="text-sm text-blue-600 mt-1">
                Configure module and submodule access for each role. System roles cannot be deleted but permissions can be customized.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {roles.map((role) => (
          <Card key={role.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${getRoleBadgeColor(role.name)}`}>
                    <Shield size={20} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{role.name}</CardTitle>
                    <CardDescription className="text-sm">{role.description}</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {role.is_system && <Badge variant="outline" className="text-xs">System</Badge>}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  <span className="font-medium text-gray-700">{countPermissions(role.modules)}</span> permissions
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleEditRole(role)}>
                    <Edit size={14} className="mr-1" /> Edit
                  </Button>
                  {!role.is_system && (
                    <Button variant="destructive" size="sm" onClick={() => { setSelectedRole(role); setDeleteDialogOpen(true); }}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Role Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield size={20} /> Edit Permissions - {selectedRole?.name}
            </DialogTitle>
            <DialogDescription>{selectedRole?.description}</DialogDescription>
          </DialogHeader>

          {selectedRole && (
            <div className="space-y-4">
              {selectedRole.name === 'Admin' && (
                <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg text-sm text-orange-700">
                  ⚠️ Admin role has full access and cannot be modified.
                </div>
              )}

              <PermissionsTable
                modules={selectedRole.modules}
                onPermissionChange={(modId, perm, checked) => handleModulePermissionChange(modId, perm, checked, true)}
                onSelectAll={(modId, subIds, checked) => handleSelectAllModule(modId, subIds, checked, true)}
                disabled={selectedRole.name === 'Admin'}
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveRole} disabled={loading || selectedRole?.name === 'Admin'}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Role Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus size={20} /> Create New Role
            </DialogTitle>
            <DialogDescription>Define a new role with custom permissions</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Role Name *</Label>
                <Input
                  value={newRoleData.name}
                  onChange={(e) => setNewRoleData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Accountant"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={newRoleData.description}
                  onChange={(e) => setNewRoleData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Role description"
                />
              </div>
            </div>

            <div>
              <Label className="text-base font-semibold">Permissions</Label>
              <p className="text-sm text-gray-500 mb-3">Select modules and permissions for this role</p>
              <PermissionsTable
                modules={newRoleData.modules}
                onPermissionChange={(modId, perm, checked) => handleModulePermissionChange(modId, perm, checked, false)}
                onSelectAll={(modId, subIds, checked) => handleSelectAllModule(modId, subIds, checked, false)}
                disabled={false}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateRole} disabled={loading}>
              {loading ? 'Creating...' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Role</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedRole?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteRole} disabled={loading}>
              {loading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RolesMaster;
